/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { slug } from '../../../common';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import AchievementUsersHero from './AchievementUsersHero';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import Link from 'flarum/common/components/Link';
import AchievementUsersListState from '../states/AchievementUsersListState';
import Button from 'flarum/common/components/Button';
import Placeholder from 'flarum/common/components/Placeholder';

// Make translation calls shorter
const t = (...args) => app.translator.trans(...args);
const prfx = `${slug}.forum.achievement`;

export default class AchievementUsersPage extends Page {
	oninit(vnode) {
		super.oninit(vnode);

		const gamificationEnabled = app.forum.attribute(`${slug}.gamification.enabled`);
		if (!gamificationEnabled) {
			m.route.set('/');
		}

		this.levelId = m.route.param('levelId');

		this.state = new AchievementUsersListState(this.levelId);

		app.history.push('achievement');
		this.bodyClass = 'App--achievement';

		this.loading = false;
		this.refresh();
	}

	view() {
		const { achievementLevel, loading, state } = this;

		let loadingUI = null;

		if (state.isInitialLoading() || state.isLoadingNext() || this.loading) {
			loadingUI = <LoadingIndicator />;
		} else if (state.hasNext()) {
			loadingUI = (
				<Button
					className="Button Button--primary"
					onclick={async () => {
						await state.loadNext();
						m.redraw();
					}}
				>
					{t(`${prfx}.load_more_button`)}
				</Button>
			);
		}

		if (state.isEmpty()) {
			loadingUI = <Placeholder text={t(`${prfx}.be_the_first`)} />;
		}

		return (
			<div className="AchievementUsersPage">
				<AchievementUsersHero achievementLevel={achievementLevel} loading={loading} />
				<div className="container">
					<div className="AchievementUsersPage-users">
						{state.getAllItems().map((user) => (
							<Link
								href={app.route('user.achievements', {
									username: user.slug(),
								})}
							>
								<div className="AchievementUsersPage-user">
									{avatar(user)}
									{username(user)}
								</div>
							</Link>
						))}
					</div>
					<div className="AchievementUsersPage-loadMore">{loadingUI}</div>
				</div>
			</div>
		);
	}

	async refresh() {
		this.loading = true;
		m.redraw();

		try {
			this.achievementLevel = await app.store.find('achievement_levels', this.levelId);
			await this.state.refresh();
		} catch (e) {
			// console.error(e);
		} finally {
			this.loading = false;
			m.redraw();
		}
	}
}
